import { useMemo, useState } from 'react';
import * as _ from 'lodash';
import { IVoucherHolderForList } from '@nkr/models/index';
import { VoucherHolderType } from '@nkr/enums/index';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ColumnApi, FirstDataRenderedEvent, GridApi, GridReadyEvent, ICellRendererParams, IHeaderParams } from 'ag-grid-community';
import ActionButtonRenderer from './action-button-renderer';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';
import { Glyphicon } from 'react-bootstrap';
import { VoucherHolderAction } from '../voucher-holder-action';
import GroupRowInnerRenderer from './group-row-inner-renderer';
import FileIcon from '@nkr/views/donors/profile/vouchers/file-icon';

interface IProps {
    list: IVoucherHolderForList[],
    centerId: number,
    isAdmin: boolean,
    isAdminLogin: boolean,
    isLoading: boolean,
    groupByDonor: boolean,
    onOrderBuccalSwabKit: (id: number) => void,
    onOrderLabScreening: (id: number) => void,
    onReorderLabScreening: (id: number) => void,
    onUploadVoucherConsent: (id: number) => void,
    onUploadSwabResults: (id: number) => void,
    onUploadScreeningResults: (id: number) => void,
    onResendLink: (id: number) => void,
    onDownload: (voucherHolderId: number, docType: number) => void
    onLoadingComplete: () => void,
    onGridReady: (e: GridReadyEvent) => void,
}

const CommonList = (props: IProps) => {
    let menuDisplayed = false;

    const [gridApi, setGridApi] = useState<GridApi>(null);
    const [columnApi, setColumnApi] = useState<ColumnApi>(null);
    const [menuId, setMenuId] = useState(0);
    const [columnDefs, setColumnDefs] = useState<ColDef[]>([
        {
            field: 'donorAlias',
            rowGroup: props.groupByDonor,
            hide: true,
            showRowGroup: true
        },
        {
            colId: 'donorCenterAlias',
            field: 'donorCenterAlias',
            sortable: false,
            width: 160,
            hide: (!props.isAdmin && !props.isAdminLogin) || !props.groupByDonor,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Donor Center</SortableHeaderComponent>
        },
        {
            colId: 'dateAdded',
            field: 'dateAdded',
            sortable: false,
            width: 140,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Date Added</SortableHeaderComponent>
        },
        {
            field: 'donorDonationDateTime',
            rowGroup: false,
            hide: true,
            showRowGroup: false,
            aggFunc: 'first'
        },
        {
            colId: 'firstName',
            field: 'firstName',
            sortable: false,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>First Name</SortableHeaderComponent>
        },
        {
            colId: 'lastName',
            field: 'lastName',
            sortable: false,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Last Name</SortableHeaderComponent>
        },
        {
            colId: 'birthDate',
            field: 'birthDate',
            sortable: false,
            width: 140,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Birth Date</SortableHeaderComponent>
        },
        {
            colId: 'files',
            sortable: false,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Files</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IVoucherHolderForList;

                return (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <FileIcon
                            voucherHolderId={data.id}
                            docType={0}
                            fileName="Voucher Consent File"
                            value={data.adpInformedConsentFile}
                            inProcess={false}
                            onDownload={props.onDownload}
                        />

                        {data.typeId != VoucherHolderType.StandardVoucher && (
                            <FileIcon
                                voucherHolderId={data.id}
                                docType={1}
                                fileName="HLA Swab Results File"
                                value={data.hlaSwabResultsFile}
                                inProcess={data.swabsOrderedDateTime != ''}
                                onDownload={props.onDownload}
                            />
                        )}

                        {data.typeId != VoucherHolderType.StandardVoucher && (
                            <FileIcon
                                voucherHolderId={data.id}
                                docType={2}
                                fileName="Screening Results File"
                                value={data.screeningResultsFile}
                                inProcess={data.lastScreeningOrderedDateTime != ''}
                                onDownload={props.onDownload}
                            />
                        )}
                    </div>
                );
            }
        },
        {
            colId: 'type',
            field: 'type',
            sortable: false,
            width: 70,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Type</SortableHeaderComponent>
        },
        {
            colId: 'status',
            field: 'status',
            sortable: false,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Status</SortableHeaderComponent>
        },
        {
            colId: 'recipient',
            sortable: false,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Recipient Record</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                let data = params.data as IVoucherHolderForList;

                if (data != null) {
                    if (data.recipientId > 0) {
                        if (data.donorCenterId == data.recipientCenterId) {
                            return <a href={`center/${data.recipientCenterId}/recipient/${data.recipientId}/profile/general`}>{data.recipientAlias}</a>
                        } else {
                            return <>{data.recipientAlias} @ {data.recipientCenterAlias}</>
                        }
                    }

                    return <div>--</div>;
                }

                return null;
            }
        },
        {
            colId: 'actionMenu',
            width: 80,
            cellClass: 'action-menu',
            suppressSizeToFit: true,
            headerComponentFramework: (params: IHeaderParams) => <SortableHeaderComponent {...params}>Actions</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                if (params.data != null) {
                    return (
                        <div style={{ width: '100%', textAlign: 'center' }}>
                            <ActionButtonRenderer {...params}
                                pullRight={true}
                                isAdmin={props.isAdmin}
                                isAdminLogin={props.isAdminLogin}
                                data={params.data}
                                onMenuToggle={handleMenuToggle}
                                onItemSelected={handleItemSelected} />
                        </div>
                    );
                }

                return null;
            }
        }
    ]);

    const onGridReady = (e: GridReadyEvent) => {
        setGridApi(e.api);
        setColumnApi(e.columnApi);

        e.api.sizeColumnsToFit();

        props.onGridReady(e);
    };

    const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    };

    const onBodyScroll = () => {
        if (menuDisplayed) {
            document.body.click();
            menuDisplayed = false;
        }
    };

    const handleMenuToggle = (show: boolean) => {
        menuDisplayed = show;
    };

    const handleItemSelected = (id: number, action: VoucherHolderAction) => {
        switch (action) {
            case VoucherHolderAction.OrderBuccalSwabKit: props.onOrderBuccalSwabKit(id); break;
            case VoucherHolderAction.OrderLabScreening: props.onOrderLabScreening(id); break;
            case VoucherHolderAction.ReorderLabScreening: props.onReorderLabScreening(id); break;
            case VoucherHolderAction.UploadVoucherConsent: props.onUploadVoucherConsent(id); break;
            case VoucherHolderAction.UploadSwabResults: props.onUploadSwabResults(id); break;
            case VoucherHolderAction.UploadScreeningResults: props.onUploadScreeningResults(id); break;
            case VoucherHolderAction.ResendLink: props.onResendLink(id); break;
        }
    };

    const renderFileIcon = (fileName: string, value: string, href: string, inProcess: boolean) => {
        if (value != '') {
            return <a href={href} target='_blank'><span className='label label-success' title={fileName}><Glyphicon glyph='ok' /></span></a>;
        } else {
            if (inProcess) {
                return <span className='label label-warning' title={fileName + ' Requested'}><Glyphicon glyph='time' /></span>;
            } else {
                return <span className='label label-danger' title={fileName + ' NOT Uploaded'}><Glyphicon glyph='remove' /></span>;
            }
        }
    };

    const list = _.sortBy(props.list.length > 0 ? props.list : (props.isLoading ? null : []), x => x.donorAlias);

    const rowData = useMemo(() => list, [props.list]);

    return (
        <AgGridReact
            defaultColDef={{
                cellClass: 'vh-list-cell',
                headerClass: 'vh-list-header',
            }}
            columnDefs={columnDefs}
            headerHeight={28}
            groupHeaderHeight={30}
            rowHeight={28}
            rowData={rowData}
            suppressCellSelection={true}
            groupDisplayType={'groupRows'}
            groupDefaultExpanded={1}
            groupSelectsChildren={true}
            suppressContextMenu={true}
            overlayNoRowsTemplate={`No voucher holders found...`}
            getRowNodeId={(data: IVoucherHolderForList) => data.id.toString()}
            groupRowRendererParams={{
                innerRenderer: GroupRowInnerRenderer,
                suppressCount: true,
            }}
            onGridReady={onGridReady}
            onFirstDataRendered={onFirstDataRendered}
            onBodyScroll={onBodyScroll}
        />
    );
}

export default CommonList;